import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Sliders,
  CheckCircle2,
  Eye,
  EyeOff,
  Save,
  RotateCcw,
  Shield,
  Clock,
  Package,
  Truck,
  Warehouse,
  Calendar,
} from 'lucide-react';
import {
  fetchLogisticsConfig,
  updateLogisticsConfig,
  fetchEtatsRoster,
  fetchLivreursRoster,
  type LogisticsConfig,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Badge } from '../ui/data.js';
import { Panel, PanelBody } from '../ui/panels.js';
import { Loading } from '../ui/feedback.js';

export function LogisticsConfigPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const configQuery = useQuery({
    queryKey: ['shipping', 'config'],
    queryFn: fetchLogisticsConfig,
  });

  const etatsQuery = useQuery({
    queryKey: ['shipping', 'etats-roster'],
    queryFn: fetchEtatsRoster,
  });

  const livreursQuery = useQuery({
    queryKey: ['shipping', 'livreurs-roster'],
    queryFn: fetchLivreursRoster,
  });

  // Local state for editing
  const [hiddenEtats, setHiddenEtats] = useState<number[]>([0]);
  const [hiddenCarriers, setHiddenCarriers] = useState<string[]>([]);
  const [hiddenLivreurs, setHiddenLivreurs] = useState<number[]>([]);
  const [excludePending, setExcludePending] = useState<boolean>(true);
  const [anomalyDays, setAnomalyDays] = useState<number>(3);
  const [returnStartDate, setReturnStartDate] = useState<string>('2026-01-01');
  const [savedSuccess, setSavedSuccess] = useState<boolean>(false);

  // Sync initial data from query
  useEffect(() => {
    if (configQuery.data?.config) {
      const cfg = configQuery.data.config;
      setHiddenEtats(cfg.hiddenEtats ?? [0]);
      setHiddenCarriers(cfg.hiddenCarriers ?? []);
      setHiddenLivreurs(cfg.hiddenLivreurs ?? []);
      setExcludePending(cfg.excludePendingFromReconciliation ?? true);
      setAnomalyDays(cfg.returnAnomalyDays ?? 3);
      setReturnStartDate(cfg.returnStartDate ?? '2026-01-01');
    }
  }, [configQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (updated: Partial<LogisticsConfig>) => updateLogisticsConfig(updated),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 4000);
    },
  });

  const handleSave = () => {
    saveMutation.mutate({
      hiddenEtats,
      hiddenCarriers,
      hiddenLivreurs,
      excludePendingFromReconciliation: excludePending,
      returnAnomalyDays: anomalyDays,
      returnStartDate,
    });
  };

  const handleResetDefaults = () => {
    setHiddenEtats([0]);
    setHiddenCarriers([]);
    setHiddenLivreurs([]);
    setExcludePending(true);
    setAnomalyDays(3);
    setReturnStartDate('2026-01-01');
  };

  const toggleEtat = (code: number) => {
    setHiddenEtats((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const toggleCarrier = (name: string) => {
    setHiddenCarriers((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name],
    );
  };

  const toggleLivreur = (id: number) => {
    setHiddenLivreurs((prev) =>
      prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id],
    );
  };

  if (configQuery.isLoading || etatsQuery.isLoading || livreursQuery.isLoading) {
    return <Loading label="Chargement des configurations logistiques…" />;
  }

  const etats = etatsQuery.data?.etats ?? [];
  const carriers = livreursQuery.data?.carriers ?? [];
  const livreurs = livreursQuery.data?.livreurs ?? [];

  return (
    <div className="space-y-6 animate-fade">
      <PageHeader
        index="S42"
        eyebrow="Gestion Logistique · Configuration"
        title="Configuration & Filtres d'Affichage"
        purpose="Personnalisez la visibilité des transporteurs, livreurs et statuts Shiparon dans les rapports, tableaux de bord et réconciliations."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetDefaults}
              className="text-ink-soft hover:text-ink"
            >
              <RotateCcw className="h-4 w-4 mr-1.5" />
              Réinitialiser
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className="bg-emerald-700 hover:bg-emerald-800 text-white font-medium shadow-xs"
            >
              <Save className="h-4 w-4 mr-1.5" />
              {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer les Réglages'}
            </Button>
          </div>
        }
      />
      
      {savedSuccess && (
        <div className="p-4 rounded-xl bg-herb-50 border border-herb-600/30 text-herb-900 flex items-center justify-between animate-fade shadow-2xs">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-herb-700 shrink-0" />
            <div>
              <p className="font-semibold text-sm">Configuration logistique mise à jour avec succès</p>
              <p className="text-xs text-herb-700">Les tableaux de bord, calculs de réconciliation et listes de colis appliquent désormais vos règles de visibilité.</p>
            </div>
          </div>
        </div>
      )}

      {/* Regle Metier Majeure : Etat 0 & 8 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Règle État 0 : En attente */}
        <Panel className="border-amber-300/80 bg-amber-50/40">
          <PanelBody className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-100 text-amber-900 shrink-0">
                <Warehouse className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-sm text-ink">État 0 · En attente (Non sortis de l'entrepôt)</h3>
                  <Badge tone="muted">Règle MaPara</Badge>
                </div>
                <p className="text-xs text-ink-soft leading-relaxed">
                  Les colis créés dans Shiparon et étiquetés qui n'ont <strong>jamais quitté l'entrepôt MaPara</strong> ne doivent pas fausser la réconciliation transporteur ni le taux de succès.
                </p>
              </div>
            </div>

            <label className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white/80 border border-amber-300/60 cursor-pointer hover:bg-white transition-colors">
              <input
                type="checkbox"
                checked={excludePending}
                onChange={(e) => setExcludePending(e.target.checked)}
                className="rounded border-border text-emerald-600 focus:ring-emerald-500 h-4 w-4"
              />
              <span className="text-xs font-semibold text-ink">
                Exclure automatiquement '0 - En attente' du total de réconciliation transporteur
              </span>
            </label>
          </PanelBody>
        </Panel>

        {/* Attention État 8 : Au magasin / dépôt */}
        <Panel className="border-sky-300/80 bg-sky-50/40">
          <PanelBody className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-sky-100 text-sky-900 shrink-0">
                <Package className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-sm text-ink">État 8 · Au magasin / dépôt (Point d'attention)</h3>
                  <Badge tone="scan">Surveillance</Badge>
                </div>
                <p className="text-xs text-ink-soft leading-relaxed">
                  Colis prêts pour départ ou stockés au dépôt transporteur. Une attention particulière est portée à ces colis car ils sont prêts pour expédition mais potentiellement immobilisés.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/80 border border-sky-300/60 text-xs">
              <span className="text-ink-soft">Volume actuel en État 8 :</span>
              <span className="font-bold text-sky-800">
                {(etats.find((e) => e.code === 8)?.count ?? 0).toLocaleString('fr-FR')} colis
              </span>
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Seuil d'anomalie des Retours (État 7) */}
      <Panel>
        <PanelBody className="space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-rust-100 text-rust-900 shrink-0">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm text-ink">Délai Maximum de Rapatriement des Retours (État 7 - Rtn agence)</h3>
                <p className="text-xs text-ink-soft mt-0.5">
                  Lorsqu'un colis est en retour et déposé à l'agence transporteur, MaPara doit le recevoir sous <strong>{anomalyDays} jours</strong>. Au-delà, il est classé en <strong>Anomalie Retard</strong>.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input
                type="number"
                min="1"
                max="30"
                value={anomalyDays}
                onChange={(e) => setAnomalyDays(Math.max(1, Number(e.target.value) || 3))}
                className="w-16 px-2 py-1 text-center font-bold text-sm border border-border rounded-lg bg-card text-ink"
              />
              <span className="text-xs text-ink-soft font-medium">jours</span>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Date de début du suivi des Retours & Délais */}
      <Panel className="border-emerald-300/80 bg-emerald-50/20">
        <PanelBody className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 text-emerald-900 shrink-0">
                <Calendar className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-sm text-ink">Date de début de suivi des Retours & Anomalies (+3j)</h3>
                  <Badge tone="good">Filtre d'historique</Badge>
                </div>
                <p className="text-xs text-ink-soft leading-relaxed max-w-2xl">
                  Définit la date à partir de laquelle les colis en retour (États 5, 7, 11, 30, 31) sont surveillés pour la détection d'anomalies de délai (+3 jours) et affichés dans la console de retours. Permet d'ignorer les retours archivés des années antérieures (ex: Janvier 2026).
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:items-end gap-2 shrink-0">
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={returnStartDate}
                  onChange={(e) => setReturnStartDate(e.target.value)}
                  className="px-3 py-1.5 font-bold text-xs border border-border rounded-lg bg-card text-ink shadow-2xs focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600"
                />
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                <button
                  type="button"
                  onClick={() => setReturnStartDate('2026-01-01')}
                  className={`px-2 py-0.5 rounded font-semibold transition-colors ${
                    returnStartDate === '2026-01-01'
                      ? 'bg-emerald-700 text-white shadow-2xs'
                      : 'bg-emerald-100 text-emerald-900 hover:bg-emerald-200'
                  }`}
                >
                  Jan 2026
                </button>
                <button
                  type="button"
                  onClick={() => setReturnStartDate('2026-02-01')}
                  className={`px-2 py-0.5 rounded transition-colors ${
                    returnStartDate === '2026-02-01'
                      ? 'bg-emerald-700 text-white font-semibold'
                      : 'bg-cream text-ink-soft hover:text-ink border border-border'
                  }`}
                >
                  Fév 2026
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    setReturnStartDate(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
                  }}
                  className="px-2 py-0.5 rounded bg-cream text-ink-soft hover:text-ink border border-border transition-colors"
                >
                  1er ce mois
                </button>
                <button
                  type="button"
                  onClick={() => setReturnStartDate('')}
                  className={`px-2 py-0.5 rounded transition-colors ${
                    !returnStartDate
                      ? 'bg-emerald-700 text-white font-semibold'
                      : 'bg-cream text-ink-soft hover:text-ink border border-border'
                  }`}
                >
                  Tout l'historique
                </button>
              </div>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Section 1: Statuts Shiparon visibles / masqués */}
      <Panel>
        <div className="p-4 border-b border-border/80 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-sm text-ink flex items-center gap-2">
              <Sliders className="h-4 w-4 text-emerald-700" />
              Visibilité des Statuts Shiparon ({etats.length} statuts)
            </h3>
            <p className="text-xs text-ink-soft mt-0.5">
              Sélectionnez les statuts à afficher ou masquer dans les vues de synthèse et les filtres opérationnels.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => setHiddenEtats([])}
              className="text-emerald-700 hover:underline font-medium"
            >
              Tout afficher
            </button>
            <span className="text-ink-ghost">·</span>
            <button
              type="button"
              onClick={() => setHiddenEtats(etats.map((e) => e.code))}
              className="text-rust-700 hover:underline font-medium"
            >
              Tout masquer
            </button>
          </div>
        </div>
        <PanelBody className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {etats.map((etat) => {
              const isHidden = hiddenEtats.includes(etat.code);
              return (
                <div
                  key={etat.code}
                  onClick={() => toggleEtat(etat.code)}
                  className={`p-3 rounded-xl border transition-all cursor-pointer select-none flex items-center justify-between ${
                    isHidden
                      ? 'bg-cream/40 border-border/60 opacity-60 text-ink-ghost'
                      : 'bg-white border-border hover:border-emerald-600/40 shadow-2xs text-ink'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div
                      className={`h-7 w-7 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 ${
                        isHidden ? 'bg-cream text-ink-ghost' : 'bg-emerald-50 text-emerald-800'
                      }`}
                    >
                      {etat.code}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold truncate">{etat.label}</p>
                      <p className="text-[11px] text-ink-soft">
                        {etat.count.toLocaleString('fr-FR')} colis enregistrés
                      </p>
                    </div>
                  </div>
                  <div className="shrink-0 ml-2">
                    {isHidden ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink-ghost bg-cream px-2 py-0.5 rounded-full">
                        <EyeOff className="h-3 w-3" /> Masqué
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-herb-800 bg-herb-50 px-2 py-0.5 rounded-full">
                        <Eye className="h-3 w-3" /> Visible
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </PanelBody>
      </Panel>

      {/* Section 2: Prestataires / Transporteurs visibles / masqués */}
      <Panel>
        <div className="p-4 border-b border-border/80 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-sm text-ink flex items-center gap-2">
              <Truck className="h-4 w-4 text-emerald-700" />
              Transporteurs & Prestataires ({carriers.length})
            </h3>
            <p className="text-xs text-ink-soft mt-0.5">
              Cochez les transporteurs actifs à afficher dans les sélecteurs et calculs de réconciliation.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => setHiddenCarriers([])}
              className="text-emerald-700 hover:underline font-medium"
            >
              Tout afficher
            </button>
            <span className="text-ink-ghost">·</span>
            <button
              type="button"
              onClick={() => setHiddenCarriers(carriers.map((c) => c.name))}
              className="text-rust-700 hover:underline font-medium"
            >
              Tout masquer
            </button>
          </div>
        </div>
        <PanelBody className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {carriers.map((carrier) => {
              const isHidden = hiddenCarriers.includes(carrier.name);
              return (
                <div
                  key={carrier.name}
                  onClick={() => toggleCarrier(carrier.name)}
                  className={`p-3 rounded-xl border transition-all cursor-pointer select-none flex items-center justify-between ${
                    isHidden
                      ? 'bg-cream/40 border-border/60 opacity-60 text-ink-ghost'
                      : 'bg-white border-border hover:border-emerald-600/40 shadow-2xs text-ink'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-xs font-bold truncate">{carrier.name}</p>
                    <p className="text-[11px] text-ink-soft">
                      {carrier.count.toLocaleString('fr-FR')} expéditions
                    </p>
                  </div>
                  <div className="shrink-0 ml-2">
                    {isHidden ? (
                      <EyeOff className="h-4 w-4 text-ink-ghost" />
                    ) : (
                      <Eye className="h-4 w-4 text-emerald-700" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </PanelBody>
      </Panel>

      {/* Section 3: Livreurs individuels (Table livreur) */}
      {livreurs.length > 0 && (
        <Panel>
          <div className="p-4 border-b border-border/80 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-sm text-ink flex items-center gap-2">
                <Shield className="h-4 w-4 text-emerald-700" />
                Livreurs Individuels ({livreurs.length} enregistrés)
              </h3>
              <p className="text-xs text-ink-soft mt-0.5">
                Gérez la visibilité des livreurs individuels pour les rapports de tournées.
              </p>
            </div>
          </div>
          <PanelBody className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-80 overflow-y-auto pr-1">
              {livreurs.map((livreur) => {
                const isHidden = hiddenLivreurs.includes(livreur.livreurId);
                return (
                  <div
                    key={livreur.livreurId}
                    onClick={() => toggleLivreur(livreur.livreurId)}
                    className={`p-2.5 rounded-lg border text-xs cursor-pointer select-none flex items-center justify-between ${
                      isHidden
                        ? 'bg-cream/40 border-border/60 opacity-60 text-ink-ghost'
                        : 'bg-white border-border hover:border-emerald-600/40'
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="font-bold truncate">{livreur.nom ?? `Livreur #${livreur.livreurId}`}</p>
                      <p className="text-[11px] text-ink-soft">{livreur.prestation ?? 'Interne'}</p>
                    </div>
                    {isHidden ? (
                      <EyeOff className="h-3.5 w-3.5 text-ink-ghost shrink-0 ml-1" />
                    ) : (
                      <Eye className="h-3.5 w-3.5 text-emerald-700 shrink-0 ml-1" />
                    )}
                  </div>
                );
              })}
            </div>
          </PanelBody>
        </Panel>
      )}

      {/* Floating Save Button Bottom Bar */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <Button
          variant="primary"
          size="md"
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold px-6 shadow-xs"
        >
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending ? 'Enregistrement en cours…' : 'Sauvegarder la Configuration'}
        </Button>
      </div>
    </div>
  );
}
